import React, { useEffect, useMemo } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import cx from 'classnames';
import {
  isDemoEventSlug,
  isLayoutMock,
  routes,
  URL,
} from 'appConstants';
import {
  ButtonV4,
  HTMLContent,
  Link,
  Preloader,
} from 'components';
import { useRequest } from 'hooks';
import type { Event, GetResponse } from 'types';
import { RequestStatus } from 'types';
import {
  dateToUiFormat,
  handleNoImage,
  getEventsFromDate,
  getEventCardTag,
  hasEventDetails,
  isEventFromCalendarPeriod,
  isEventRegistrationOpen,
  openApplicationForm,
  _has_t_back,
  _t,
  _t_back,
} from 'utils';

import banner from 'assets/img/main-v4/hero-curve.png';
import gallery1 from 'assets/img/events-v4/gallery-1.png';
import gallery2 from 'assets/img/events-v4/gallery-2.png';
import iconPlus from 'assets/img/main-v4/icon-plus.svg';

import styles from './styles.module.scss';

const RELATED_LIMIT = 3;

const TITLE_FALLBACK = (
  'Международная промышленная выставка "ИННОПРОМ. Центральная Азия" – Узбекистан'
);

const LEAD_FALLBACK = (
  'Весной 2026 года Центр поддержки и развития экспорта\u00a0«Моспром» организует ' +
  'участие столичных экспортеров промышленной продукции в рамках коллективного ' +
  'стенда на международной промышленной выставке «ИННОПРОМ. Центральная Азия» в ' +
  'Узбекистане.'
);

const BEFORE_GALLERY_FALLBACK = [
  'Продукцию от российских производителей из 43 регионов представляют на ' +
  'фестивале-ярмарке «Сделано в России», которую Российский экспортный центр ' +
  '(Группа ВЭБ.РФ) организует в преддверии китайского Нового года в городе ' +
  'Шэньян, столице провинции Ляонин. Фестиваль проходит с 17 по 25 января в ' +
  'крупнейшем торговом центре города',
  'Всего жители Шэньяна могут оценить продукцию от более 120 производителей: ' +
  'продукты питания, напитки, косметику, сувениры и товары народного промысла. ' +
  'Так, например, на прилавках ярмарки можно будет найти молочную продукцию из ' +
  'Воронежской области, приморский мед, кондитерские изделия из Белгородской ' +
  'области и Кабардино-Балкарской Республики, владимирское печенье, бакалею из ' +
  'Амурской области, морсы из Карелии, медовые торты из Иркутской области ' +
  'косметику от новгородского производителя    и многое другое.',
];

const AFTER_GALLERY_FALLBACK = [
  'Гости могут попробовать настоящие русские блюда в кафе, оценить российскую ' +
  'продукцию в дегустационной зоне и сразу же приобрести понравившиеся продукты. ' +
  'Приобрести российскую продукцию также можно в национальных магазинах РЭЦ на ' +
  'китайских маркетплейсах. Кроме того, на площадке ярмарки компании проведут ' +
  'деловые встречи с представителями китайского бизнеса.',
  '\u00a0«Расширение знаний китайских потребителей и бизнеса о том, насколько ' +
  'разнообразную продукцию Россия готова поставлять, необходимо для реализации ' +
  'потенциала нашего торгового сотрудничества. Фестиваль-ярмарка “Сделано в ' +
  'России” предоставляет производителям площадку для того, чтобы оценить спрос ' +
  'на свою продукцию, найти покупателей и партнеров, сделать еще один шаг к ' +
  'высокой узнаваемости на новом рынке»,',
  'Напомним, Российский экспортный центр в 2024 году организовал три ' +
  'фестиваля-ярмарки «Сделано в России» в Китае. Так, в преддверии китайского ' +
  'Нового года первая ярмарка прошла в крупнейших городах Шэньян и Далянь в ' +
  'провинции Ляонин. Второе мероприятие состоялось в мае в городе Харбин, ' +
  'провинция Хэйлунцзян. Третья ярмарка прошла в ноябре в столице провинции ' +
  'Сычуань — городе Чэнду.\u00a0',
];

const formatRange = (start?: string, end?: string) => {
  const a = dateToUiFormat(start || '');
  const b = dateToUiFormat(end || '');
  if (a && b) {
    return `${a} - ${b}`;
  }
  return a || b || '';
};

type Props = {
  crumbs?: Array<{ label: string, to: string }>,
  embedded?: boolean,
};

const EventsDetails = ({ crumbs, embedded = false }: Props) => {
  const { id } = useParams<{ id: string }>();
  const layoutMock = isLayoutMock() || isDemoEventSlug(id);
  const { data, status, request } = useRequest<Event>({
    errback: () => undefined,
  });
  const {
    data: relatedData,
    request: relatedRequest,
  } = useRequest<GetResponse<Event[]>>({
    errback: () => undefined,
  });

  useEffect(() => {
    if (layoutMock) {
      return;
    }
    request({
      method: 'get',
      url: `${URL.EVENTS.GET}/${id}`,
    });
    relatedRequest({
      method: 'get',
      url: URL.EVENTS.GET,
      params: {
        limit: RELATED_LIMIT + 10,
        start_filter_date: getEventsFromDate(),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const showFallback = layoutMock;

  const title = showFallback
    ? _t(TITLE_FALLBACK)
    : (_t_back(data, 'title') || _t('Мероприятие'));
  const lead = showFallback ? _t(LEAD_FALLBACK) : '';
  const beforeGallery = showFallback
    ? BEFORE_GALLERY_FALLBACK.map((text) => _t(text))
    : [];
  const afterGallery = showFallback
    ? AFTER_GALLERY_FALLBACK.map((text) => _t(text))
    : [];
  const article = showFallback ? '' : (_t_back(data, 'description') || '');
  const city = showFallback
    ? _t('ТАШКЕНТ')
    : (
      _t_back(data?.new_country?.[0]?.location, 'title') ||
      _t_back(data?.new_country?.[0], 'name') ||
      _t_back(data?.country?.[0], 'title') ||
      _t_back(data, 'location') ||
      ''
    ).toUpperCase();
  const dateRange = showFallback
    ? '20.04.2026 - 22.04.2026'
    : formatRange(data?.date_of_event_start, data?.date_of_event_end);

  let gallery: string[] = [];
  if (showFallback) {
    gallery = [gallery1, gallery2];
  } else if (data?.image && hasEventDetails(data)) {
    gallery = [data.image];
  }

  const related = useMemo(() => {
    const items = layoutMock
      ? []
      : (relatedData?.results || [])
        .filter((item) => (
          _has_t_back(item) &&
          isEventFromCalendarPeriod(item) &&
          String(item.slug) !== String(id) &&
          item.id !== data?.id
        ))
        .slice(0, RELATED_LIMIT);

    if (items.length) {
      return items.map((item) => ({
        id: item.id,
        slug: item.slug,
        tag: _t(getEventCardTag(item)),
        title: _t_back(item, 'title'),
        meta: [
          formatRange(item.date_of_event_start, item.date_of_event_end)
            .replace(/(\d{2})\.(\d{2})\.(\d{4})/g, '$1.$2'),
          _t_back(item, 'location'),
        ].filter(Boolean).join('\n'),
        canLink: hasEventDetails(item),
        canRegister: isEventRegistrationOpen(item),
      }));
    }

    if (!layoutMock) {
      return [];
    }

    return [1, 2, 3].map((n) => ({
      id: n,
      slug: `demo-event-${n + 1}`,
      tag: _t('форум'),
      title: _t('Форум будущих технологий “Биоэкономика”'),
      meta: _t('26.02 - 27.02\nМосква, Конгресс-Центр Международной Торговли'),
      canLink: false,
      canRegister: true,
    }));
  }, [relatedData, id, data?.id, layoutMock]);

  if (!layoutMock && status === RequestStatus.SUCCESS && data && !hasEventDetails(data)) {
    return <Redirect to={routes.events.root} />;
  }

  return (
    <div className={cx(styles.page, embedded && styles.pageEmbedded)}>
      {!embedded ? (
        <div className={styles.banner} aria-hidden>
          <img src={banner} alt="" />
        </div>
      ) : null}

      <Preloader isLoading={status === RequestStatus.REQUEST}>
        <section className={styles.head}>
          <h1 className={styles.title}>{title || _t('Мероприятие')}</h1>
          <nav className={styles.crumbs} aria-label={_t('Навигация')}>
            <Link to={routes.main.root} className={styles.crumbLink}>{_t('Главная')}</Link>
            <span className={styles.crumbLine} aria-hidden />
            {(crumbs && crumbs.length ? crumbs : [
              { label: _t('Мероприятия'), to: routes.events.root },
            ]).map((crumb) => (
              <React.Fragment key={`${crumb.to}-${crumb.label}`}>
                <Link to={crumb.to} className={styles.crumbLink}>
                  {crumb.label}
                </Link>
                <span className={styles.crumbLine} aria-hidden />
              </React.Fragment>
            ))}
            <span className={styles.crumbCurrent}>{title}</span>
          </nav>
        </section>

        <div className={styles.layout}>
          <article className={styles.article}>
            <div className={styles.meta}>
              {city ? <span className={styles.cityBadge}>{city}</span> : null}
              {dateRange ? <span className={styles.dateBadge}>{dateRange}</span> : null}
            </div>

            <div className={styles.body}>
              {lead ? <p className={styles.leadStrong}>{lead}</p> : null}
              {beforeGallery.map((text) => (
                <p
                  key={text.slice(0, 40)}
                  className={text.length > 400 ? styles.leadWide : styles.lead}
                >
                  {text}
                </p>
              ))}

              {gallery.length ? (
                <div className={styles.gallery}>
                  {gallery.map((src) => (
                    <img
                      key={src}
                      src={src}
                      className={gallery.length > 1 ? styles.photoSplit : styles.photo}
                      alt={title}
                      onError={handleNoImage}
                    />
                  ))}
                </div>
              ) : null}

              {afterGallery.map((text, index) => (
                <p
                  key={text.slice(0, 48)}
                  className={index === 0 ? styles.lead : styles.leadMid}
                >
                  {text}
                </p>
              ))}

              {article ? (
                <HTMLContent content={article} className={styles.html} />
              ) : null}
            </div>
          </article>

          <aside className={styles.aside}>
            <h2 className={styles.asideTitle}>{_t('Смотрите также')}</h2>
            <ul className={styles.asideList}>
              {related.map((item) => {
                const to = `${routes.events.root}/${item.slug || item.id}`;
                return (
                  <li key={item.id} className={styles.asideCard}>
                    <span className={styles.asideBadge}>{item.tag}</span>
                    <div className={styles.asideBody}>
                      {item.canLink ? (
                        <Link to={to} className={styles.asideCardTitle}>{item.title}</Link>
                      ) : (
                        <span className={styles.asideCardTitle}>{item.title}</span>
                      )}
                      <p className={styles.asideMeta}>{item.meta}</p>
                      {item.canRegister ? (
                        <ButtonV4
                          type="button"
                          label="Регистрация"
                          variant="outline"
                          tone="onLight"
                          iconSrc={iconPlus}
                          onClick={() => openApplicationForm({
                            type: 'event',
                            id: item.id,
                            title: item.title,
                            path: to,
                          })}
                        />
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </aside>
        </div>
      </Preloader>
    </div>
  );
};

export default EventsDetails;
